import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { PageHeader, StatusDot, Empty } from "../components/Crud";
import { CalendarDays, Users, GraduationCap, FolderKanban, ListChecks, Flame, ChevronRight, Sparkles, Info, Trophy, Megaphone, Instagram, Facebook, Linkedin, Twitter } from "lucide-react";
import Tour, { shouldShowTour } from "../components/Tour";
import GetStarted from "../components/GetStarted";
import StreakCelebration from "../components/StreakCelebration";

export default function Dashboard() {
    const { t } = useTranslation();
    const { user, effectiveRole } = useAuth();
    // Honour founder "View As" mode: dashboard content mirrors the
    // sidebar's effective role, not the account's real role.
    const role = effectiveRole || user?.role;
    const [data, setData] = useState(null);
    const [streak, setStreak] = useState(null);
    const [habitsToday, setHabitsToday] = useState(null);
    const [assessments, setAssessments] = useState([]);
    const [classes, setClasses] = useState([]);
    const [projects, setProjects] = useState([]);
    const [socialPosts, setSocialPosts] = useState([]);
    const [showTour, setShowTour] = useState(false);
    useEffect(() => {
        api.get("/dashboard").then((r) => setData(r.data));
        api.post("/streak/ping").then((r) => setStreak(r.data)).catch(() => {});
        api.get("/habits/grid").then((r) => setHabitsToday({ done: r.data.habits_completed_today, total: r.data.habits_total })).catch(() => {});
        api.get("/social_posts").then(r => setSocialPosts(r.data)).catch(() => {});
        if (role === "student") {
            api.get("/assessments").then(r => setAssessments(r.data)).catch(() => {});
            api.get("/classes").then(r => setClasses(r.data)).catch(() => {});
        } else if (role === "professional") {
            api.get("/projects").then(r => setProjects(r.data)).catch(() => {});
        }
        if (role && shouldShowTour()) {
            // Wait for the verify-email banner to be resolved before starting
            // the tour. If the user is still unverified the banner is
            // interactive and the tour's dim overlay would otherwise swallow
            // the "Verify now" click. (Carry-forward from iter 9.)
            if (!user.email_verified) return;
            const t = setTimeout(() => setShowTour(true), 800);
            return () => clearTimeout(t);
        }
    }, [user, role]);
    const isStudent = role === "student";
    const isPro = role === "professional";

    const stats = data?.counts || {};
    const tasks = data?.tasks || [];
    const events = data?.events || [];

    const now = new Date();
    const weekAhead = new Date(now.getTime() + 7 * 24 * 3600 * 1000);
    const inRange = (d) => d >= now && d <= weekAhead;

    // Items for the rail — student
    const studentItems = (() => {
        if (!isStudent) return [];
        const classById = Object.fromEntries(classes.map(c => [c.id, c]));
        const items = [];
        events.forEach(e => {
            if (!e.start) return;
            const d = new Date(e.start);
            if (inRange(d)) items.push({ id: `e-${e.id}`, when: d, title: e.title, kind: e.class_id ? "class" : "event", link_to: e.link_to || "/calendar" });
        });
        assessments.forEach(a => {
            if (!a.due || a.archived) return;
            const d = new Date(`${a.due}T23:59`);
            if (inRange(d)) items.push({ id: `a-${a.id}`, when: d, title: a.title + (a.class_id && classById[a.class_id] ? ` · ${classById[a.class_id].code || classById[a.class_id].name}` : ""), kind: "assessment", link_to: a.class_id ? `/classes/${a.class_id}` : "/projects" });
        });
        tasks.forEach(t => {
            if (!t.due || t.status === "complete") return;
            const d = new Date(t.due);
            if (!isNaN(d) && inRange(d)) items.push({ id: `t-${t.id}`, when: d, title: t.title, kind: "task", link_to: t.project_id ? `/projects/${t.project_id}` : "/todo" });
        });
        return items.sort((a, b) => a.when - b.when).slice(0, 5);
    })();

    // Items for the rail — professional
    const proItems = (() => {
        if (!isPro) return [];
        const items = [];
        projects.forEach(p => {
            if (!p.end_date || p.archived) return;
            const d = new Date(p.end_date);
            if (!isNaN(d) && inRange(d)) items.push({ id: `p-${p.id}`, when: d, title: p.title + (p.client_name ? ` · ${p.client_name}` : ""), kind: "deadline", link_to: `/projects/${p.id}` });
        });
        events.forEach(e => {
            if (!e.start) return;
            const d = new Date(e.start);
            if (inRange(d)) items.push({ id: `e-${e.id}`, when: d, title: e.title, kind: "event", link_to: e.link_to || "/calendar" });
        });
        tasks.forEach(t => {
            if (!t.due || t.status === "complete") return;
            const d = new Date(t.due);
            if (!isNaN(d) && inRange(d)) items.push({ id: `t-${t.id}`, when: d, title: t.title, kind: "task", link_to: t.project_id ? `/projects/${t.project_id}` : "/todo" });
        });
        return items.sort((a, b) => a.when - b.when).slice(0, 5);
    })();

    const railItems = isStudent ? studentItems : proItems;
    const railTitle = isStudent ? t("dashboard.railTitleStudent") : t("dashboard.railTitlePro");
    const railSubtitle = isStudent ? t("dashboard.railSubStudent") : t("dashboard.railSubPro");
    const emptyCta = isStudent
        ? { hint: t("dashboard.emptyHintStudent"), action: t("dashboard.emptyCtaStudent"), to: "/classes" }
        : { hint: t("dashboard.emptyHintPro"), action: t("dashboard.emptyCtaPro"), to: "/projects" };

    return (
        <div data-testid="dashboard-page">
            <PageHeader
                title={t("dashboard.greeting", { name: user?.name?.split(" ")[0] || "" })}
                subtitle={isStudent ? t("dashboard.subtitleStudent") : t("dashboard.subtitlePro")}
                action={
                    <Link to={isStudent ? "/classes" : "/clients"} className="btn-ghost text-sm" data-testid="dash-cta-primary">
                        {t("common.open")} {isStudent ? t("nav.classes") : t("nav.clients")}
                    </Link>
                }
            />
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8 fade-up">
                <StatCard to={isStudent ? "/classes" : "/clients"} icon={isStudent ? GraduationCap : Users}
                          label={isStudent ? t("dashboard.statClasses") : t("dashboard.statClients")} value={isStudent ? stats.classes : stats.clients}
                          testid="stat-clients" />
                <StatCard to="/projects" icon={FolderKanban} label={t("dashboard.statProjects")} value={stats.projects} testid="stat-projects" />
                <StatCard to="/todo" icon={ListChecks} label={t("dashboard.statTasks")} value={stats.tasks_open} testid="stat-tasks" />
                <StatCard to="/calendar" icon={CalendarDays} label={t("dashboard.statEvents")} value={stats.events} testid="stat-events" />
                <StreakCard streak={streak} habitsToday={habitsToday} t={t} />
            </div>

            <GetStarted role={role} streak={streak} />
            <StreakCelebration streak={streak} />

            {(isStudent || isPro) && (
                <div className="mb-8 fade-up delay-2" data-testid="week-rail">
                    <div className="flex items-baseline justify-between mb-3">
                        <div>
                            <div className="font-script text-[var(--brand-orange)] text-lg">{railSubtitle}</div>
                            <h2 className="font-serif text-2xl">{railTitle}</h2>
                        </div>
                        <Link to="/calendar" className="text-xs text-[var(--text-ink)]/65 hover:text-[var(--brand-purple)] inline-flex items-center gap-1" data-testid="week-see-all">
                            {t("dashboard.seeAll")} <ChevronRight size={14} />
                        </Link>
                    </div>
                    {railItems.length === 0 ? (
                        <div className="card-warm p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="week-empty">
                            <div className="w-12 h-12 rounded-2xl brand-gradient grid place-items-center text-white shrink-0">
                                <Sparkles size={20} />
                            </div>
                            <div className="flex-1">
                                <div className="font-serif text-lg">{t("dashboard.blankWeek")}</div>
                                <div className="text-sm text-[var(--text-ink)]/65 mt-0.5">{emptyCta.hint}</div>
                            </div>
                            <Link to={emptyCta.to} className="btn-primary text-sm" data-testid="week-empty-cta">
                                {emptyCta.action}
                            </Link>
                        </div>
                    ) : (
                        <div className="flex gap-3 overflow-x-auto scrollbar-thin pb-2 -mx-1 px-1">
                            {railItems.map((item) => {
                                const accent = item.kind === "assessment" ? "var(--brand-magenta)"
                                            : item.kind === "class" ? "var(--brand-orange)"
                                            : item.kind === "deadline" ? "var(--brand-magenta)"
                                            : item.kind === "task" ? "var(--status-red)" : "var(--brand-purple)";
                                const label = item.kind === "assessment" ? t("dashboard.kindAssessment")
                                            : item.kind === "class" ? t("dashboard.kindClass")
                                            : item.kind === "deadline" ? t("dashboard.kindDeadline")
                                            : item.kind === "task" ? t("dashboard.kindTask") : t("dashboard.kindEvent");
                                const w = item.when;
                                const dayShort = w.toLocaleDateString("en-GB", { weekday: "short" });
                                const dayNum = w.getDate();
                                const time = w.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
                                return (
                                    <Link key={item.id} to={item.link_to}
                                          data-testid={`week-item-${item.id}`}
                                          className="card-warm p-4 w-56 shrink-0 hover:-translate-y-0.5 transition-transform"
                                          style={{ borderTop: `3px solid ${accent}` }}>
                                        <div className="flex items-start gap-3">
                                            <div className="text-center">
                                                <div className="text-[10px] uppercase tracking-wide text-[var(--text-ink)]/55">{dayShort}</div>
                                                <div className="font-serif text-2xl leading-none" style={{ color: accent }}>{dayNum}</div>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-[10px] uppercase tracking-wide" style={{ color: accent }}>{label}</div>
                                                <div className="font-medium text-sm leading-tight line-clamp-2">{item.title}</div>
                                                <div className="text-[11px] text-[var(--text-ink)]/55 mt-1">{time}</div>
                                            </div>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            <div className="grid lg:grid-cols-12 gap-6">
                <div className="lg:col-span-7 card-warm p-6 fade-up delay-2" data-testid="dash-agenda">
                    <div className="font-serif text-2xl mb-3">{t("dashboard.agenda")}</div>
                    {tasks.length === 0 ? (
                        <Empty icon={ListChecks} title={t("dashboard.noTasks")} hint={t("dashboard.noTasksHint")} />
                    ) : (
                        <ul className="space-y-2">
                            {tasks.slice(0, 8).map((t) => (
                                <li key={t.id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-[rgba(255,107,53,0.06)] transition">
                                    <StatusDot status={t.status || "incomplete"} />
                                    <Link to={t.project_id ? `/projects/${t.project_id}` : "/todo"}
                                          className="flex-1 truncate" data-testid={`agenda-task-${t.id}`}>
                                        <div className="text-sm font-medium">{t.title}</div>
                                        {t.due && <div className="text-xs text-[var(--text-ink)]/60">{t.due}</div>}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="lg:col-span-5 card-warm p-6 fade-up delay-3" data-testid="dash-upcoming">
                    <div className="font-serif text-2xl mb-3">{t("dashboard.upcoming")}</div>
                    {events.length === 0 ? (
                        <Empty icon={CalendarDays} title={t("dashboard.nothingScheduled")} hint={t("dashboard.addFromCalendar")} />
                    ) : (
                        <ul className="space-y-2">
                            {events.slice(0, 8).map((e) => (
                                <li key={e.id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-[rgba(123,45,142,0.06)] transition">
                                    <StatusDot status="appointment" />
                                    <Link to={e.link_to || "/calendar"} className="flex-1 truncate" data-testid={`agenda-event-${e.id}`}>
                                        <div className="text-sm font-medium">{e.title}</div>
                                        <div className="text-xs text-[var(--text-ink)]/60">{e.start}</div>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            <UpcomingSocialRail posts={socialPosts} />
            {showTour && <Tour role={role} onClose={() => setShowTour(false)} />}
        </div>
    );
}

function StatCard({ to, icon: Icon, label, value, testid }) {
    return (
        <Link to={to} className="card-warm p-4 hover:-translate-y-0.5 transition-transform" data-testid={testid}>
            <Icon size={18} className="text-[var(--brand-purple)] mb-2" />
            <div className="text-xs uppercase tracking-wide text-[var(--text-ink)]/60">{label}</div>
            <div className="font-serif text-3xl">{value ?? 0}</div>
        </Link>
    );
}

function StreakCard({ streak, habitsToday, t }) {
    const current = streak?.current ?? 0;
    const best = streak?.best ?? 0;
    const todayActive = streak?.today;
    const lastActive = streak?.last_active;
    const hot = current >= 3;
    const dayWord = current === 1 ? t("streak.day") : t("streak.days");
    const explainer = current === 0
        ? t("streak.startPrompt")
        : t("streak.usedFor", { count: current });
    const formatDate = (iso) => {
        if (!iso) return "—";
        try {
            return new Date(iso + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "short" });
        } catch {
            return iso;
        }
    };
    // Highest celebrated milestone (7 / 30 / 100), or fall back to `best` if it has crossed a milestone
    const milestones = [100, 30, 7];
    let achievement = null;
    if (typeof window !== "undefined") {
        achievement = milestones.find((m) => localStorage.getItem(`vu_streak_celebrated_${m}`)) || null;
    }
    if (!achievement) achievement = milestones.find((m) => best >= m) || null;
    return (
        <Link
            to="/personal#habits"
            className="card-warm p-4 relative overflow-hidden group block transition hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[var(--brand-orange)]/40 rounded-2xl"
            data-testid="stat-streak"
            aria-label={t("streak.ariaLabel", { count: current, explainer })}
            title={`${current} ${dayWord} — ${explainer}`}
            style={hot ? { background: "linear-gradient(135deg, rgba(255,107,53,0.12), rgba(233,30,99,0.10))" } : undefined}
        >
            <div className="flex items-start justify-between">
                <Flame
                    size={18}
                    className="mb-2"
                    style={{ color: hot ? "var(--brand-orange)" : "var(--text-ink)", opacity: hot ? 1 : 0.5 }}
                />
                <Info size={13} className="text-[var(--text-ink)]/40 group-hover:text-[var(--brand-orange)] transition" data-testid="streak-info-icon" />
            </div>
            {achievement && (
                <div
                    className="absolute -top-1 right-9 inline-flex items-center gap-1 text-[9px] font-medium px-1.5 py-0.5 rounded-full text-white shadow-sm"
                    style={{ background: "linear-gradient(92deg, var(--brand-purple), var(--brand-magenta))" }}
                    data-testid="streak-best-badge"
                    title={t("streak.personalBest", { n: achievement })}
                >
                    <Trophy size={9} /> {t("streak.clubBadge", { n: achievement })}
                </div>
            )}
            <div className="text-xs uppercase tracking-wide text-[var(--text-ink)]/60">{current === 1 ? t("streak.dayTitle") : t("streak.daysTitle")}</div>
            <div className="flex items-baseline gap-1">
                <div
                    className="font-serif text-3xl"
                    data-testid="streak-current"
                    style={{ color: hot ? "var(--brand-orange)" : "var(--text-ink)" }}
                >
                    {current}
                </div>
                <div className="text-xs text-[var(--text-ink)]/55">
                    {current === 1 ? t("streak.day") : t("streak.days")}
                </div>
            </div>

            <div className="mt-2 grid grid-cols-2 gap-1.5 text-[11px]" data-testid="streak-stats">
                <div className="rounded-md bg-[rgba(26,19,24,0.04)] px-2 py-1">
                    <div className="text-[9px] uppercase tracking-wider text-[var(--text-ink)]/55">{t("streak.best")}</div>
                    <div className="font-medium text-[var(--text-ink)]" data-testid="streak-best">{best} {best === 1 ? t("streak.day") : t("streak.days")}</div>
                </div>
                <div className="rounded-md bg-[rgba(26,19,24,0.04)] px-2 py-1">
                    <div className="text-[9px] uppercase tracking-wider text-[var(--text-ink)]/55">{t("streak.lastActive")}</div>
                    <div className="font-medium text-[var(--text-ink)]" data-testid="streak-last-active">{todayActive ? t("streak.todayLabel") : formatDate(lastActive)}</div>
                </div>
                {habitsToday && habitsToday.total > 0 && (
                    <div className="col-span-2 rounded-md bg-[rgba(255,107,53,0.08)] px-2 py-1 flex items-center gap-1.5" data-testid="streak-habits-today">
                        <span className="text-[9px] uppercase tracking-wider text-[var(--brand-orange)]">{t("streak.habitsToday")}</span>
                        <span className="font-medium text-[var(--text-ink)] tabular-nums">{habitsToday.done} / {habitsToday.total} ✓</span>
                    </div>
                )}
            </div>

            <div className="text-[11px] text-[var(--text-ink)]/60 mt-2 leading-snug" data-testid="streak-explainer">
                {explainer}
            </div>
            <div className="text-[10px] text-[var(--brand-purple)] mt-1 inline-flex items-center gap-0.5 group-hover:gap-1 transition-all">
                {t("streak.openHabits")} <ChevronRight size={11} />
            </div>
        </Link>
    );
}


const PLATFORM_ICON = { instagram: Instagram, facebook: Facebook, linkedin: Linkedin, x: Twitter };
const PLATFORM_COLOR = { instagram: "#E1306C", facebook: "#1877F2", linkedin: "#0A66C2", x: "#0F1419" };

function UpcomingSocialRail({ posts }) {
    const { t } = useTranslation();
    const now = new Date();
    const horizon = new Date(now.getTime() + 14 * 24 * 3600 * 1000);
    const upcoming = (posts || [])
        .filter(p => p.scheduled_at && p.status !== "posted" && p.status !== "skipped")
        .map(p => ({ ...p, _when: new Date(p.scheduled_at) }))
        .filter(p => !isNaN(p._when) && p._when >= now && p._when <= horizon)
        .sort((a, b) => a._when - b._when)
        .slice(0, 6);

    if (!posts || posts.length === 0) return null;

    return (
        <div className="mt-8 fade-up delay-3" data-testid="dash-upcoming-social">
            <div className="flex items-baseline justify-between mb-3">
                <div>
                    <h2 className="font-serif text-2xl">{t("dashboard.upcomingSocial")}</h2>
                </div>
                <Link to="/social" className="text-xs text-[var(--text-ink)]/65 hover:text-[var(--brand-purple)] inline-flex items-center gap-1" data-testid="social-see-all">
                    {t("dashboard.seeAll")} <ChevronRight size={14} />
                </Link>
            </div>
            {upcoming.length === 0 ? (
                <div className="card-warm p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="dash-social-empty">
                    <div className="w-12 h-12 rounded-2xl brand-gradient grid place-items-center text-white shrink-0">
                        <Megaphone size={20} />
                    </div>
                    <div className="flex-1">
                        <div className="font-serif text-lg">{t("dashboard.socialNothingScheduled")}</div>
                    </div>
                    <Link to="/social" className="btn-primary text-sm" data-testid="dash-social-empty-cta">{t("dashboard.socialPlanCta")}</Link>
                </div>
            ) : (
                <div className="flex gap-3 overflow-x-auto scrollbar-thin pb-2 -mx-1 px-1">
                    {upcoming.map(p => {
                        const w = p._when;
                        const dayShort = w.toLocaleDateString("en-GB", { weekday: "short" });
                        const dayNum = w.getDate();
                        const time = w.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
                        const accent = p.status === "scheduled" ? "var(--brand-orange)" : "var(--brand-purple)";
                        return (
                            <Link key={p.id} to="/social"
                                  data-testid={`dash-social-card-${p.id}`}
                                  className="card-warm p-4 w-64 shrink-0 hover:-translate-y-0.5 transition-transform"
                                  style={{ borderTop: `3px solid ${accent}` }}>
                                <div className="flex items-start gap-3">
                                    <div className="text-center shrink-0">
                                        <div className="text-[10px] uppercase tracking-wide text-[var(--text-ink)]/55">{dayShort}</div>
                                        <div className="font-serif text-2xl leading-none" style={{ color: accent }}>{dayNum}</div>
                                        <div className="text-[10px] text-[var(--text-ink)]/55 mt-0.5">{time}</div>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1 mb-1">
                                            {(p.platforms || []).slice(0, 4).map(k => {
                                                const Icon = PLATFORM_ICON[k];
                                                return Icon ? <Icon key={k} size={11} style={{ color: PLATFORM_COLOR[k] }} /> : null;
                                            })}
                                            <span className="text-[10px] uppercase tracking-wide ml-auto" style={{ color: accent }}>{p.status}</span>
                                        </div>
                                        <div className="font-medium text-sm leading-tight line-clamp-2">
                                            {p.campaign_name || p.caption?.slice(0, 60) || "Untitled post"}
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
